import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../lib/supabase';
import { TICKETMASTER_CONFIG } from '../lib/ticketmaster/config';
import type { TicketmasterConfig } from '../lib/ticketmaster/types';
import { logger } from '../lib/logger';
import { HOTEL_ID } from '../lib/supabase/configClient';
import { useSection } from './useSection';

export const useTicketmasterQuery = () => {
  const section = useSection();

  return useQuery({
    queryKey: ['ticketmaster-config', section?.id],
    queryFn: async () => {
      try {
        if (!section?.id) {
          logger.error('Missing section ID', {
            category: 'API',
            data: { section }
          });
          throw new Error('Section ID is required');
        }

        if (section.type_rubrique !== 'event-api' || section.API !== 'ticketmaster') {
          logger.error('Invalid section configuration', {
            category: 'API',
            data: { 
              type: section.type_rubrique,
              API: section.API,
              sectionId: section.id
            }
          });
          throw new Error('Invalid section type or API');
        }

        logger.debug('Fetching Ticketmaster config', {
          category: 'API',
          data: { 
            sectionId: section.id,
            sectionType: section.type_rubrique,
            sectionName: section.names.fr,
            API: section.API
          }
        });

        // Get config from Supabase using section ID
        const { data: config, error: configError } = await supabaseClient
          .from('ticketmaster_config_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', section.id)
          .single();

        if (configError) {
          logger.error('Error fetching Ticketmaster config', {
            category: 'API',
            data: { 
              error: configError,
              hotelId: HOTEL_ID,
              sectionId: section.id,
              query: {
                table: 'ticketmaster_config_section',
                filters: {
                  hotel_id: HOTEL_ID,
                  section_id: section.id
                }
              }
            }
          });
          throw configError;
        }

        if (!config) {
          logger.error('No config found', {
            category: 'API',
            data: { 
              hotelId: HOTEL_ID,
              sectionId: section.id,
              query: {
                table: 'ticketmaster_config_section',
                filters: {
                  hotel_id: HOTEL_ID,
                  section_id: section.id
                }
              }
            }
          });
          throw new Error(`No Ticketmaster configuration found for section ID: ${section.id}`);
        }

        logger.debug('Ticketmaster config loaded', {
          category: 'API',
          data: { 
            sectionId: section.id,
            config: {
              radius: config.radius,
              size: config.size,
              locale: config.locale,
              translationTableId: config.translationtableid,
              classificationIds: config.classificationids,
              excludedGenreIds: config.excludedgenreids,
              rateLimits: {
                day: config.ratelimitday,
                minute: config.ratelimitminute
              }
            }
          }
        });

        // Return formatted config
        return {
          radius: Number(config.radius) || 10,
          size: Number(config.size) || 100,
          locale: config.locale || 'fr',
          translationTableId: config.translationtableid,
          classificationIds: config.classificationids?.split(',').map(id => id.trim()).filter(Boolean) || [],
          excludedGenreIds: config.excludedgenreids?.split(',').map(id => id.trim()) || TICKETMASTER_CONFIG.EXCLUDED_GENRES,
          apiKey: TICKETMASTER_CONFIG.API.KEY,
          baseUrl: TICKETMASTER_CONFIG.API.BASE_URL,
          rateLimits: {
            day: Number(config.ratelimitday) || TICKETMASTER_CONFIG.API.RATE_LIMIT.DAY,
            minute: Number(config.ratelimitminute) || TICKETMASTER_CONFIG.API.RATE_LIMIT.MINUTE
          }
        } as TicketmasterConfig;

      } catch (error) {
        logger.error('Error loading Ticketmaster config', {
          category: 'API',
          data: { 
            error,
            errorDetails: error instanceof Error ? {
              message: error.message,
              stack: error.stack
            } : error,
            sectionId: section?.id
          }
        });
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: !!section?.id && section.type_rubrique === 'event-api' && section.API === 'ticketmaster'
  });
};